import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { notifications } from '@mantine/notifications';
import { getUniqueColumnsWithValues, getUniqueLookupColumnsWithValues } from './helpers';
import useStructuredUnstructuredSelection from './useStructuredUnstructuredSelection';

export const useExtractionTransformationTableHelper = ({
  funds,
  data,
  onClearSearch,
  isLookup,
  fundMappings,
  setPickedChildFund,
  setPickedFund,
  onChangeFunds,
  selectedFund,
  response,
  headerIndex,
  setSelectedFund,
  pickedFund,
  pickedChildFund,
  activeFundChildValue,
  setActiveFundChildValue,
  isIndividualFund,
  footerIndex
}) => {
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [error, setError] = useState({});
  const [focusedCell, setFocusedCell] = useState(null);
  const [rowPinning, setRowPinning] = useState({
    top: []
  });
  const [columnPinning, setColumnPinning] = useState({
    left: []
  });
  const mutatedFunds = useMemo(() => {
    return funds?.length > 0 ? funds : response?.funds;
  }, [funds, response]);

  function popupErrorMessage(columnId, msg, type) {
    setError({
      id: columnId,
      text: msg,
      type: type || 'error'
    });

    setTimeout(() => {
      setError({});
    }, 3000);
  }

  const { onSelectionStructuredCells, onSelectionUnStructuredCells } =
    useStructuredUnstructuredSelection({
      funds,
      onChangeFunds,
      isIndividualFund,
      isLookup,
      activeFundChildValue,
      fundMappings,
      setPickedFund,
      setSelectedFund,
      setPickedChildFund,
      selectedColumn,
      setSelectedColumn,
      popupErrorMessage
    });

  useEffect(() => {
    return () => {
      setSelectedTable(null);
      setSelectedColumn(null);
      setError({});
      setFocusedCell(null);
      setRowPinning({
        top: []
      });
      setColumnPinning({
        left: []
      });
    };
  }, []);

  const onSelectFund = id => {
    const item = selectedFund.find(item => item === id);
    if (item) {
      setSelectedFund(selectedFund.filter(item => item !== id));
    } else {
      setSelectedFund([...selectedFund, id]);
    }
    setActiveFundChildValue(null);
  };

  const onSelectTable = useCallback(value => {
    setSelectedTable(value);
    onClearSearch();
  }, []);

  const onSelectColumn = useCallback(
    value => {
      setSelectedColumn(value);
      onClearSearch();
      // if (isLookup) setActiveFundChildValue(value);
    },
    [isLookup]
  );

  const onClearFundValues = useCallback(
    columnId => {
      const _funds = cloneDeep(funds);
      if (isLookup) {
        _funds.forEach(fund => {
          const columnToClear = fund.columns.find(column => column.id === columnId);

          if (columnToClear) {
            columnToClear.values = columnToClear.values.map(item => ({
              ...item,
              rowId: null,
              columnId: null,
              text: null
            }));
          }
        });
      } else
        _funds.forEach(fund => {
          fund.tables.forEach(table => {
            const columnToClear = table.columns.find(column =>
              column?.is_custom ? column.uuid === columnId : column.id === columnId
            );

            if (columnToClear) {
              columnToClear.values = columnToClear.values.map(item => ({
                ...item,
                rowId: null,
                columnId: null,
                text: null
              }));
            }
          });
        });
      onChangeFunds(_funds);
    },
    [funds, isLookup]
  );

  const onFullSelectionStructuredCells = useCallback(() => {
    let _funds = cloneDeep(funds);

    if (!isLookup) {
      const allCols = getUniqueColumnsWithValues(funds);
      allCols.forEach(_colMap => {
        const startIndex = headerIndex || 0;
        const endIndex = footerIndex || data?.length;
        const _formatedValues = data?.slice(startIndex, endIndex)?.map((item, idx) => {
          const value = item[_colMap.values[0].columnId - 1];

          const _payload = {
            rowId: idx + startIndex + 1,
            columnId: _colMap.values[0].columnId,
            text: value
          };

          return _payload;
        });

        const hasOneMappedFund =
          (fundMappings.not_mapped_source_funds.length === 0 && funds.length === 1) ||
          fundMappings?.fund_mappings?.length === 1;

        const hasOneMappedFundOrIndividualFund = hasOneMappedFund || isIndividualFund;

        const _columnTypes = _colMap.type;

        for (let i = 0; i < _funds.length; i++) {
          let _fund = _funds[i];

          const values = _formatedValues.filter(value => value.text === _fund.source_fund_name);

          for (let j = 0; j < _funds[i].tables.length; j++) {
            let _table = _funds[i].tables[j];

            for (let c = 0; c < _table.columns.length; c++) {
              let _column = _table.columns[c];

              if (hasOneMappedFundOrIndividualFund) {
                if (_column.id === _colMap.id || _column.id === _colMap?.name?.toLowerCase()) {
                  const _filteredItems = _formatedValues.filter(el =>
                    _columnTypes.includes(typeof el.text)
                  );
                  const hasInvalidTypeValue = _formatedValues.find(
                    el => !_columnTypes.includes(typeof el.text)
                  );

                  if (
                    hasInvalidTypeValue &&
                    _colMap.id !== 'fund name' &&
                    _column.id !== 'fund name'
                  )
                    notifications.show({
                      title: 'Invalid value types skipped',
                      color: 'orange',
                      withBorder: true,
                      position: 'bottom-center',
                      autoClose: 3000
                    });

                  _column.values = _filteredItems;

                  break;
                }
              } else {
                if (
                  _column.id === 'fund name' &&
                  _colMap.id !== 'fund name' &&
                  _column.values.length === 0
                ) {
                  notifications.show({
                    title: 'Please map Fund name first, then select the other fields',
                    color: 'red',
                    withBorder: true,
                    position: 'bottom-center',
                    autoClose: 3000
                  });

                  return;
                }

                if (_column.id === 'fund name' && _column.id === _colMap.id) {
                  _column.values = values.map(val => ({
                    ...val,
                    rowId: val.rowId,
                    text: _fund.name
                  }));

                  break;
                }

                if (_column.id === _colMap.id) {
                  const _fundColumn = _table.columns.find(val => val.id === 'fund name');
                  const _rowsId = _fundColumn.values.map(r => r.rowId);
                  const result = _formatedValues.filter(val => _rowsId.includes(val.rowId));

                  _column.values = result;

                  break;
                }
              }
            }
          }
        }
      });

      onChangeFunds(_funds);
      setPickedFund(funds.map(item => item.id));
      setSelectedFund(funds.map(item => item.id));
      setPickedChildFund(
        funds
          .map(item => item.tables)
          .flat()
          .map(item => item?.id)
      );
      setSelectedColumn(null);
    } else {
      const allCols = getUniqueLookupColumnsWithValues(funds);
      let _formatedValues;

      allCols.forEach(_colMap => {
        _formatedValues = [
          ...(_formatedValues || []),
          ...data
            ?.slice(Math.max((headerIndex || 1) - 1, 0), footerIndex || data?.length)
            ?.map((item, idx) => {
              const value = item[_colMap.values[0].columnId - 1];

              const _payload = {
                rowId: idx + Math.max((headerIndex || 1) - 1, 0) + 1,
                columnId: _colMap.values[0].columnId,
                text: value
              };

              return _payload;
            })
        ];
      });

      _funds = _funds.map(_fund => {
        _fund.columns = _fund.columns.map(_col => {
          const colId = _col.values.find(_val => !!_val.columnId)?.columnId;
          const _filteredValues = _formatedValues.filter(val => val.columnId === colId);

          if (_filteredValues.length) _col.values = [..._filteredValues];

          return _col;
        });

        return _fund;
      });

      onChangeFunds(_funds);
    }

    toast.success('Remapping Completed ');
  }, [funds, data, headerIndex, footerIndex, isLookup, isIndividualFund]);

  const onClearValuesColumn = useCallback(
    (fundId, tableId, columnId) => {
      const _funds = cloneDeep(funds);
      if (isLookup) {
        for (let i = 0; i < _funds.length; i++) {
          if (_funds[i].id === fundId) {
            for (let c = 0; c < _funds[i].columns.length; c++) {
              let column = _funds[i].columns[c];
              if (column.id === columnId) {
                _funds[i].columns[c].values = _funds[i].columns[c].values.map(item => ({
                  ...item,
                  rowId: null,
                  columnId: null,
                  text: null
                }));
                onChangeFunds(_funds);
                return;
              }
            }
          }
        }
      } else {
        for (let i = 0; i < _funds.length; i++) {
          if (_funds[i].id === fundId) {
            for (let j = 0; j < _funds[i].tables.length; j++) {
              if (_funds[i].tables[j].id === tableId) {
                for (let c = 0; c < _funds[i].tables[j].columns.length; c++) {
                  let column = _funds[i].tables[j].columns[c];
                  if (column.id === columnId) {
                    _funds[i].tables[j].columns[c].values = _funds[i].tables[j].columns[
                      c
                    ].values.map(item => ({
                      ...item,
                      rowId: null,
                      columnId: null,
                      text: null
                    }));
                    onChangeFunds(_funds);
                    return;
                  }
                }
              }
            }
          }
        }
      }
    },
    [funds, isLookup]
  );

  const handleDeleteLookupSelectedCell = useCallback(
    (ind, foundChildId, id) => {
      const _funds = cloneDeep(funds);

      for (let i = 0; i < _funds.length; i++) {
        if (_funds[i].id === foundChildId) {
          for (let c = 0; c < _funds[i].columns.length; c++) {
            let column = _funds[i].columns[c];
            if (column.id === id) {
              _funds[i].columns[c].values = column.values.map((item, index) =>
                index === ind ? { ...item, rowId: null, columnId: null, text: null } : item
              );
              onChangeFunds(_funds);
              return;
            }
          }
        }
      }
    },
    [funds]
  );

  const handleDeleteSelectedCell = useCallback(
    (ind, foundChildId, id) => {
      const _funds = cloneDeep(funds);

      for (let i = 0; i < _funds.length; i++) {
        for (let j = 0; j < _funds[i].tables.length; j++) {
          if (_funds[i].tables[j].id === foundChildId) {
            for (let c = 0; c < _funds[i].tables[j].columns.length; c++) {
              let column = _funds[i].tables[j].columns[c];
              if (column.id === id) {
                column.values = column.values.map((item, index) =>
                  index === ind ? { ...item, rowId: null, columnId: null, text: null } : item
                );

                onChangeFunds(_funds);
                return;
              }
            }
          }
        }
      }
    },
    [funds]
  );

  const onDeleteSelectedCell = (ind, foundChildId, id) => {
    if (isLookup) {
      handleDeleteLookupSelectedCell(ind, foundChildId, id);
    } else {
      handleDeleteSelectedCell(ind, foundChildId, id);
    }
  };

  const onPickChildFund = id => {
    if (Array.isArray(id)) {
      setPickedChildFund(id);
      return;
    }

    const item = pickedChildFund.find(item => item === id);

    if (item) {
      setPickedChildFund(pickedChildFund.filter(item => item !== id));
    } else {
      setPickedChildFund([...pickedChildFund, id]);
    }
  };

  const onPickFund = id => {
    const item = pickedFund.find(item => item === id);
    if (item) {
      setPickedFund(pickedFund.filter(item => item !== id));
    } else {
      setPickedFund([...pickedFund, id]);
    }
  };

  const handleDeleteLookupRow = useCallback(
    (rowId, tableId) => {
      const _funds = cloneDeep(funds);

      for (let i = 0; i < _funds.length; i++) {
        if (_funds[i].id === tableId) {
          for (let c = 0; c < _funds[i].columns.length; c++) {
            let column = _funds[i].columns[c];
            _funds[i].columns[c].values = column.values.filter(item => item.rowId !== rowId);
          }
          onChangeFunds(_funds);
          return;
        }
      }
    },
    [funds]
  );

  const handleDeleteRow = useCallback((rowId, tableId) => {
    const _funds = cloneDeep(funds);

    for (let i = 0; i < _funds.length; i++) {
      for (let j = 0; j < _funds[i].tables.length; j++) {
        if (_funds[i].tables[j].id === tableId) {
          for (let c = 0; c < _funds[i].tables[j].columns.length; c++) {
            let column = _funds[i].tables[j].columns[c];
            _funds[i].tables[j].columns[c].values = column.values.filter(
              item => item.rowId !== rowId
            );
          }
          onChangeFunds(_funds);
          return;
        }
      }
    }
  }, []);

  const onDeleteRow = (rowId, tableId) => {
    if (isLookup) {
      handleDeleteLookupRow(rowId, tableId);
    } else {
      handleDeleteRow(rowId, tableId);
    }
  };

  const onFixRow = rowId => {
    setRowPinning({
      top: rowId ? Array.from(Array(rowId).keys()).map(i => String(i)) : []
    });
  };

  const onFixColumn = columnId => {
    setColumnPinning({
      left: columnId ? Array.from(Array(columnId).keys()).map(i => String(i)) : []
    });
  };

  const onFixRowAndColumn = (rowId, columnId) => {
    setRowPinning({
      top: rowId ? Array.from(Array(rowId).keys()).map(i => String(i)) : []
    });
    setColumnPinning({
      left: columnId ? Array.from(Array(columnId).keys()).map(i => String(i)) : []
    });
  };

  const onFocusedCell = useCallback(value => {
    setFocusedCell(value);
  }, []);

  return {
    error,
    focusedCell,
    selectedColumn,
    mutatedFunds,
    selectedLookupTable: selectedTable,
    onSelectFund,
    onSelectTable,
    onSelectColumn,
    onClearFundValues,
    onSelectionStructuredCells,
    onSelectionUnStructuredCells,
    onClearValuesColumn,
    onDeleteSelectedCell,
    onPickChildFund,
    onPickFund,
    onDeleteRow,
    onFixColumn,
    onFixRowAndColumn,
    onFixRow,
    onFocusedCell,
    onFullSelectionStructuredCells,
    rowPinning,
    columnPinning
  };
};
